/**
 * Générateur — Automatisme : dénombrer des cubes dans des empilements.
 * Attendu ID : 1067 | Niveau : 5ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Combien de cubes ?       (saisie sur figure ; QCM sur figure)
 *   - Par couches              (saisie sans figure)
 *   - Couches : vrai ou faux ? (V/F pool + deux lignes numériques)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°29 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUIT au CM1, CONSOLIDÉ au CM2 (problèmes sur des assemblages de
 * cubes), ÉTENDU en 6e (le dénombrement sert à déterminer un volume, comptage
 * par couches), automatisme en 5e ; la formule du volume est FORMALISÉE en 5e
 * (objectif, hors de ce gen). P2 : sens installés — compter un par un, par
 * rangées ; sens de 6e — le comptage organisé par couches (une multiplication)
 * et les cubes cachés qui comptent. Distracteurs : ne compter que les cubes
 * visibles, additionner les dimensions, la couche du bas seule, une couche de
 * trop. P3 : dimensions 2 à 5, couche ≤ 15, total ≤ 60, tables ; divisions
 * exactes. P4 : parties-tout (couche × couches) ; l'énoncé bref qui n'induit
 * pas l'opération est travaillé en actes par la saisie. P5 : perspective
 * isométrique (moteur commun svgCubes.js). P6 : aucun instrument, pas de
 * calculatrice. P7 : pas de formule V = L × l × h, pas de cm³ ni d'unité, pas
 * d'empilement dont des cubes seraient indéterminables. P8 : empilement,
 * couche, pile, rangée, cube caché, pavé plein. P9 : tables et division par un
 * chiffre restent implicites. P10 : constat (« combien »). P11 : les cubes
 * cachés comptent ; du comptage un par un à la multiplication par couches.
 * P12 : rémanence forte.
 * INVARIANTS :
 *   - le total est CALCULÉ depuis h[r][c] ; les empilements irréguliers n'ont
 *     aucun cube caché (garde du moteur) ; les pavés pleins sont annoncés
 *     « pleins » dans l'énoncé (les cubes intérieurs sont déductibles) ;
 *   - options de QCM distinctes, une seule bonne ; réponses ENTIÈRES ;
 *   - V/F : jamais de crochets, mixité 1-3 faux, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';
import { svgPerspective, aucunCubeCache, cleH } from './svgCubes';

const ATTENDU_ID = 1067;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FIGURE = 'Combien de cubes ?';
const L_COUCHES = 'Par couches';
const L_VF = 'Couches : vrai ou faux ?';

const pl = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;
const total = (h) => h.flat().reduce((a, b) => a + b, 0);
const nbPiles = (h) => h.flat().filter((v) => v > 0).length;
const visiblesDeFace = (h) => h[0].map((_, c) => Math.max(...h.map((row) => row[c]))).reduce((a, b) => a + b, 0);

// ─── Empilements ───────────────────────────────────────────────────────────
// forme : 'pave' (plein, a colonnes × b rangées × c de haut), 'colonnes' (une
// rangée de piles de hauteurs variées), 'escalier' (marches montant vers le fond).

const tirerEmpilement = (forme) => {
    for (let essai = 0; essai < 300; essai++) {
        let h;
        if (forme === 'pave') {
            const a = randInt(2, 4); const b = randInt(2, 3); const c = randInt(2, 3);
            h = Array.from({ length: b }, () => Array(a).fill(c));
        } else if (forme === 'colonnes') {
            const n = randInt(3, 4);
            h = [Array.from({ length: n }, () => randInt(1, 4))];
            if (new Set(h[0]).size < 2) continue;                       // pas une rangée uniforme
        } else {
            // n marches (rangées) de 2 à 4, largeur p de 1 à 3 : 9 silhouettes (revue 2026-09-04, A8 : 4 avant)
            const n = randInt(2, 4); const p = randInt(1, 3);
            h = Array.from({ length: n }, (_, r) => Array(p).fill(r + 1)); // r = 0 devant : la marche la plus basse
        }
        const t = total(h);
        if (t < 4 || t > 36) continue;
        // pavé et escalier sont PLEINS (annoncé dans l'énoncé) : leurs cubes intérieurs sont déductibles ;
        // la garde « aucun cube caché » ne s'applique qu'aux piles irrégulières
        if (forme === 'colonnes' && !aucunCubeCache(h)) continue;
        return h;
    }
    throw new Error('gen_1067 : empilement introuvable');
};

const DESCRIPTION = {
    pave: 'L\'empilement est un pavé plein : aucun cube ne manque à l\'intérieur.',
    colonnes: 'L\'empilement est fait de piles de cubes posées côte à côte.',
    escalier: 'L\'empilement est un escalier : chaque marche est une rangée de cubes, et les marches montent vers le fond.',
};

// ─── Carte 1a : combien de cubes ? (saisie) ────────────────────────────────

let derniereSignatureFigure1067 = null;

const genFigureSaisie = () => {
    let payload = construireFigureSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureFigure1067; t++) payload = construireFigureSaisie();
    derniereSignatureFigure1067 = payload._meta.params.signature;
    return payload;
};

const construireFigureSaisie = () => {
    const forme = randomChoice(['pave', 'colonnes', 'escalier']);
    const sousType = forme === 'pave' ? randomChoice(['total', 'couche', 'hors_sol', 'ajout']) : randomChoice(['total', 'hors_sol', 'ajout']);
    const h = tirerEmpilement(forme);
    const t = total(h); const piles = nbPiles(h);
    let question; let reponse; let ajout = 0;
    if (sousType === 'total') { question = 'Combien de cubes composent cet empilement ?'; reponse = t; }
    else if (sousType === 'couche') { question = 'Combien de cubes forment une couche horizontale de cet empilement ?'; reponse = h.length * h[0].length; }
    else if (sousType === 'hors_sol') { question = 'Combien de cubes de cet empilement ne touchent pas le sol ?'; reponse = t - piles; }
    else { ajout = randInt(2, 4); question = `Si on ajoutait à cet empilement une pile de ${ajout} cubes, combien de cubes y aurait-il en tout ?`; reponse = t + ajout; }
    const svg = svgPerspective(h, { triedre: false, echelle: 1.5, extra: ` data-forme="${forme}"` });
    const payload = makeSaisie(String(reponse), `<p>${DESCRIPTION[forme]}</p><p>${question}</p>`, {
        ...opts(L_FIGURE), suffixe: 'cubes', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { forme, sousType, h, reponse, ajout, signature: `${sousType}-${forme}-${cleH(h)}-${ajout}` };
    return payload;
};

// ─── Carte 1b : combien de cubes ? (QCM) ───────────────────────────────────

let derniereSignatureFigureQcm1067 = null;

const genFigureQcm = () => {
    let payload = construireFigureQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureFigureQcm1067; t++) payload = construireFigureQcm();
    derniereSignatureFigureQcm1067 = payload._meta.params.signature;
    return payload;
};

const construireFigureQcm = () => {
    const forme = randomChoice(['pave', 'colonnes', 'escalier']);
    // Le RANG de la bonne réponse parmi les quatre valeurs triées est tiré d'abord (revue 2026-09-04,
    // A2 : le total était une valeur du milieu dans 92 % des tirages, jamais la plus petite).
    const rang = randInt(0, 3);
    for (let essai = 0; essai < 120; essai++) {
        const h = tirerEmpilement(forme);
        const t = total(h); const couche = h.length * h[0].length; const piles = nbPiles(h); const hMax = Math.max(...h.flat());
        // leurres issus d'erreurs de comptage réelles, en dessous et au-dessus du total
        const pool = [
            { v: visiblesDeFace(h), role: 'visibles' },                 // ne compter que ce qu'on voit de face
            { v: piles, role: 'couche_bas' },                           // la couche du bas seulement
            { v: t - couche, role: 'couche_de_moins' },                 // une couche oubliée
            { v: t + couche, role: 'couche_de_trop' },                  // une couche comptée deux fois
            { v: t + 2 * couche, role: 'deux_couches_de_trop' },        // deux couches comptées deux fois
            { v: piles * hMax, role: 'hauteur_max_partout' },           // toutes les piles à la hauteur de la plus haute
            { v: t - hMax, role: 'pile_oubliee' },                      // la pile la plus haute oubliée
        ];
        if (forme === 'pave') pool.push({ v: h[0].length + h.length + h[0][0], role: 'somme_dimensions' }, { v: t + h.length * h[0][0], role: 'colonne_de_trop' });
        const valides = shuffle(pool).filter((cand) => cand.v > 0 && cand.v !== t);
        const dessous = []; const dessus = [];
        valides.forEach((cand) => {
            const cible = cand.v < t ? dessous : dessus;
            if (!dessous.some((l) => l.v === cand.v) && !dessus.some((l) => l.v === cand.v)) cible.push(cand);
        });
        // rang r : r leurres en dessous du total, 3 − r au-dessus ; après 60 essais, un rang possible pour cette figure
        let r = rang;
        if (essai >= 60) { const possibles = [0, 1, 2, 3].filter((k) => dessous.length >= k && dessus.length >= 3 - k); if (!possibles.length) continue; r = randomChoice(possibles); }
        if (dessous.length < r || dessus.length < 3 - r) continue;
        const leurres = [...dessous.slice(0, r), ...dessus.slice(0, 3 - r)];
        const options = shuffle([String(t), ...leurres.map((l) => String(l.v))]);
        const svg = svgPerspective(h, { triedre: false, echelle: 1.5, extra: ` data-forme="${forme}"` });
        const payload = makeQcm(options, String(t), `<p>${DESCRIPTION[forme]}</p><p>Combien de cubes composent cet empilement ?</p>`, {
            ...opts(L_FIGURE), elements_additionnels: imgElement(svg, 'medium'),
        });
        payload.contenu = { ...payload.contenu, sousType: forme };
        payload._meta.params = { forme, h, total: t, roles: leurres.map((l) => l.role), signature: `${forme}-${cleH(h)}` };
        return payload;
    }
    throw new Error('gen_1067 : QCM figure introuvable');
};

// ─── Carte 2a : par couches (saisie sans figure) ───────────────────────────

let derniereSignatureCouches1067 = null;

const genCouchesSaisie = () => {
    let payload = construireCouchesSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureCouches1067; t++) payload = construireCouchesSaisie();
    derniereSignatureCouches1067 = payload._meta.params.signature;
    return payload;
};

const construireCouchesSaisie = () => {
    const sousType = randomChoice(['produit', 'quotient', 'couche', 'pave']);
    let texte; let reponse; let params;
    if (sousType === 'produit') {
        const n = randInt(2, 5); const k = randInt(4, 12);
        texte = `<p>Un empilement est fait de ${pl(n, 'couche')} identiques de ${k} cubes.</p><p>Combien de cubes en tout ?</p>`;
        reponse = n * k; params = { n, k };
    } else if (sousType === 'quotient') {
        const n = randInt(2, 5); const k = randInt(4, 12); const N = n * k;
        texte = `<p>Un empilement de ${N} cubes est fait de couches identiques de ${k} cubes.</p><p>Combien y a-t-il de couches ?</p>`;
        reponse = n; params = { n, k, N };
    } else if (sousType === 'couche') {
        const L = randInt(3, 5); const l = randInt(2, 4);
        texte = `<p>Une couche horizontale d'un empilement est un rectangle de ${L} cubes de long sur ${l} cubes de large.</p><p>Combien de cubes forment cette couche ?</p>`;
        reponse = L * l; params = { L, l };
    } else {
        const L = randInt(3, 5); const l = randInt(2, 3); const hh = randInt(2, 4);
        texte = `<p>Un pavé plein de cubes mesure ${L} cubes de long, ${l} de large et ${hh} de haut.</p><p>Combien de cubes en tout ?</p>`;
        reponse = L * l * hh; params = { L, l, h: hh };
    }
    const payload = makeSaisie(String(reponse), texte, { ...opts(L_COUCHES), suffixe: sousType === 'quotient' ? 'couches' : 'cubes' });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, ...params, reponse, signature: `${sousType}-${Object.values(params).join('x')}` };
    return payload;
};

// (Le QCM « quel calcul ? » a été retiré : il portait sur la structure multiplicative, pas sur le
//  dénombrement — décision prof 2026-09-04.)

// ─── Carte 3 : couches, vrai ou faux ? ─────────────────────────────────────

const POOL_VF = [
    { l: 'Dans un pavé plein, toutes les couches horizontales ont le même nombre de cubes.', v: true },
    { l: 'Le nombre de cubes d\'un pavé plein est le nombre de cubes d\'une couche multiplié par le nombre de couches.', v: true },
    { l: 'Un cube caché derrière un autre compte quand même dans le total.', v: true },
    { l: 'Les cubes qui ne touchent pas le sol sont tous ceux qui ne sont pas dans la couche du bas.', v: true },
    { l: 'Compter les cubes par couches revient à faire une multiplication.', v: true },
    { l: 'Pour compter les cubes d\'un empilement, il suffit de compter ceux qu\'on voit sur le dessin.', v: false },
    { l: 'Le nombre de cubes d\'un pavé plein s\'obtient en additionnant sa longueur, sa largeur et sa hauteur.', v: false },
    { l: 'Dans un pavé plein, la couche du bas contient plus de cubes que les autres couches.', v: false },
    { l: 'Un empilement fait de couches identiques a toujours autant de cubes par couche que de couches.', v: false },
    { l: 'La vue de face suffit pour compter tous les cubes d\'un empilement.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 5) throw new Error('gen_1067 : pool V/F déséquilibré');
POOL_VF.forEach((it) => { if (/[[\]]/.test(it.l)) throw new Error('gen_1067 : crochets dans une ligne V/F'); });

const FAMILLE_VF_1067 = (l) => {
    if (/couches horizontales ont le même nombre|couche du bas contient plus/.test(l)) return 'couches_egales';
    if (/multiplié par le nombre de couches|additionnant sa longueur|revient à faire une multiplication/.test(l)) return 'produit';
    if (/cube caché|ceux qu'on voit sur le dessin|vue de face suffit/.test(l)) return 'caches';
    return l;
};

let derniersServisVf1067 = [];

const genVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        const vraie1 = Math.random() < 0.5; const n1 = randInt(2, 5); const k1 = randInt(4, 9);
        const vraie2 = Math.random() < 0.5; const n2 = randInt(2, 5); const k2 = randInt(4, 9);
        const numeriques = [
            { l: `Un empilement de ${pl(n1, 'couche')} identiques de ${k1} cubes contient ${vraie1 ? n1 * k1 : n1 * k1 + randomChoice([-k1, k1, n1])} cubes.`, v: vraie1 },
            { l: `Un empilement de ${n2 * k2} cubes fait de couches identiques de ${k2} cubes a ${vraie2 ? n2 : n2 + randomChoice([1, -1, k2 - n2].filter((d) => d !== 0 && n2 + d >= 1))} couches.`, v: vraie2 },
        ];
        // Les deux lignes fixes portent sur des faits DIFFÉRENTS (revue 2026-09-04, A8 : 16 % de
        // tableaux avec deux lignes d'une même famille — produit, cubes cachés, couches égales).
        const dispo = shuffle(POOL_VF.filter((it) => !derniersServisVf1067.includes(it.l)));
        const premiere = dispo[0];
        const seconde = dispo.find((it) => it !== premiere && FAMILLE_VF_1067(it.l) !== FAMILLE_VF_1067(premiere.l));
        if (!seconde) continue;
        const fixes = [premiere, seconde];
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisVf1067 = [...derniersServisVf1067, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Dénombrer des cubes dans un empilement. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1067 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    figure_saisie: genFigureSaisie,
    figure_qcm_unique: genFigureQcm,
    couches_saisie: genCouchesSaisie,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'figure', label: L_FIGURE, formatsList: buildFormats('figure') },
    { id: 'couches', label: L_COUCHES, formatsList: buildFormats('couches') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
